import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import styled from "styled-components";
import { FaPlus, FaTimes, FaUpload, FaEdit, FaTrash, FaTag } from "react-icons/fa";

// Define the backend API URL
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

// Common tags for pottery and ceramic products
const SUGGESTED_TAGS = [
  "ceramic", "pottery", "handmade", "vase", "bowl", "plate", "mug", "cup", 
  "tableware", "homeware", "decor", "stoneware", "earthenware", "porcelain", 
  "glazed", "kiln-fired", "artisan", "handcrafted", "limited", "exclusive",
  "kitchen", "dining", "gift", "sale", "new", "best-seller", "traditional", 
  "modern", "rustic", "minimalist", "decorative", "functional", "unique","pottery",
  "ceramic", "handmade", "vase", "bowl", "plate", "mug", "cup", "tableware", "homeware", "decor", "stoneware", "earthenware", "porcelain", "glazed", "kiln-fired", "artisan", "handcrafted", "limited", "exclusive","kitchen", "dining", "gift", "sale", "new", "best-seller", "traditional", 
  "modern", "rustic", "minimalist", "decorative", "functional", "unique","featured","sale"
];

// Styled Components
const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Poppins', sans-serif;
`;

const PageTitle = styled.h2`
  text-align: center;
  color: #333;
  margin-bottom: 30px;
  font-size: 28px;
`;

const FormContainer = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  padding: 30px;
  margin-bottom: 40px;
`;

const FormGrid = styled.form`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const FormSection = styled.div`
  grid-column: 1 / -1;
  border-bottom: 1px solid #eee;
  padding-bottom: 15px;
  margin-bottom: 15px;
  
  h3 {
    font-size: 18px;
    color: #444;
    margin-bottom: 15px;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 15px;
`;

const Label = styled.label`
  font-size: 14px;
  color: #555;
  margin-bottom: 5px;
`;

const Input = styled.input`
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  min-height: 120px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
  }
`;

const ImagePreviewContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
`;

const ImagePreviewItem = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #ddd;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .remove-btn {
    position: absolute;
    top: 2px;
    right: 2px;
    background: rgba(255, 255, 255, 0.8);
    border: none;
    border-radius: 50%;
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #e74c3c;
    font-size: 12px;
    
    &:hover {
      background: white;
    }
  }
`;

const UploadButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 15px;
  background-color: #f0f0f0;
  border-radius: 6px;
  font-size: 14px;
  
  svg {
    font-size: 16px;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
`;

const Tag = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background-color: #f0f0f0;
  border-radius: 30px;
  font-size: 13px;
  
  svg {
    cursor: pointer;
    color: #777;
    
    &:hover {
      color: #e74c3c;
    }
  }
`;

const TagInput = styled.div`
  display: flex;
  align-items: center;
  
  input {
    flex: 1;
    padding: 12px 15px;
    border: 1px solid #ddd;
    border-radius: 6px 0 0 6px;
    font-size: 14px;
    transition: border-color 0.3s;
    
    &:focus {
      outline: none;
      border-color: #007bff;
      box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
    }
  }
  
  button {
    padding: 12px 15px;
    background-color: #007bff;
    border: none;
    border-radius: 0 6px 6px 0;
    color: white;
    cursor: pointer;
    transition: background-color 0.3s;
    
    &:hover {
      background-color: #0056b3;
    }
    
    svg {
      font-size: 14px;
    }
  }
`;

const SubmitButton = styled.button`
  grid-column: 1 / -1;
  padding: 15px;
  background-color: ${props => props.isEdit ? '#ffa500' : '#007bff'};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: ${props => props.isLoading ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.isLoading ? 0.7 : 1};
  transition: background-color 0.3s;
  
  &:hover {
    background-color: ${props => props.isEdit ? '#e69500' : '#0056b3'};
  }
  
  &:disabled {
    background-color: #999;
  }
`;

const ProductList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const ProductCard = styled.div`
  border: 1px solid #eee;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s, box-shadow 0.3s;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const ProductImage = styled.div`
  height: 200px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ProductInfo = styled.div`
  padding: 15px;
`;

const ProductTitle = styled.h3`
  font-size: 18px;
  color: #333;
  margin-bottom: 5px;
`;

const ProductPrice = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: #007bff;
  margin-bottom: 8px;
`;

const ProductDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 12px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
`;

const ActionButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 12px;
  font-size: 14px;
  border: none;
  border-radius: 6px;
  background-color: ${props => props.variant === 'edit' ? '#ffa500' : '#e74c3c'};
  color: white;
  cursor: pointer;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: ${props => props.variant === 'edit' ? '#e69500' : '#c0392b'};
  }
  
  svg {
    font-size: 14px;
  }
`;

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 20px;
  color: #666;
  font-size: 16px;
`;

// Add a new styled component for suggested tags
const SuggestedTagsContainer = styled.div`
  margin-top: 15px;
`;

const SuggestedTagsTitle = styled.p`
  font-size: 13px;
  color: #666;
  margin-bottom: 10px;
`;

const SuggestedTagsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SuggestedTag = styled.div`
  padding: 4px 10px;
  background-color: #f5f5f5;
  border: 1px dashed #ddd;
  border-radius: 20px;
  font-size: 12px;
  color: #555;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #e6f7ff;
    border-color: #91d5ff;
    color: #1890ff;
  }
`;

const ProductAdmin = () => {
  const [products, setProducts] = useState([]);
  const [productForm, setProductForm] = useState({
    name: "",
    capacity: "",
    materialType: "",
    glaze: "",
    firingTemp: "",
    discount: "0",
    mrp: "0",
    price: "0",
    dimensions: "",
    coneType: "",
    description: "",
    imageUrl: "",
    category: "",
    stock: "0",
  });

  const [tag, setTag] = useState("");
  const [tags, setTags] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [editProduct, setEditProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = React.createRef();

  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    fetchProducts();
    
    // If ID is present in URL, load product for editing
    if (id) {
      fetchProductById(id);
    }
  }, [id]);

  const fetchProducts = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_BASE_URL}/products`);
      setProducts(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching products:", error);
      setIsLoading(false);
    }
  };

  const fetchProductById = async (productId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/products/${productId}`);
      const product = response.data;
      
      setProductForm({
        name: product.name || "",
        capacity: product.capacity || "",
        materialType: product.materialType || "",
        glaze: product.glaze || "",
        firingTemp: product.firingTemp || "",
        discount: product.discount || "0",
        mrp: product.mrp || "0",
        price: product.price || "0",
        dimensions: product.dimensions || "",
        coneType: product.coneType || "",
        description: product.description || "",
        imageUrl: "",
        category: product.category || "",
        stock: product.stock || "0",
      });
      
      // Handle both imageUrl and images fields for backward compatibility
      let productImages = [];
      
      if (product.imageUrl) {
        if (Array.isArray(product.imageUrl)) {
          productImages = product.imageUrl;
        } else if (typeof product.imageUrl === 'string' && product.imageUrl) {
          productImages = [product.imageUrl];
        }
      } else if (product.images && Array.isArray(product.images)) {
        productImages = product.images;
      }
      
      setExistingImages(productImages);
      setPreviewImages([]); // Clear new image previews
      setImageFiles([]); // Clear new image files
      setTags(product.tags || []);
      setEditProduct(product);
    } catch (error) {
      console.error("Error fetching product:", error);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setImageFiles((prevFiles) => [...prevFiles, ...files]);

    // Generate preview URLs for the new files
    const newPreviewUrls = files.map(file => URL.createObjectURL(file));
    setPreviewImages((prevPreviews) => [...prevPreviews, ...newPreviewUrls]);
  };

  const removePreviewImage = (index) => {
    // Remove from preview URLs and release the object URL
    URL.revokeObjectURL(previewImages[index]);
    
    const updatedPreviews = [...previewImages];
    updatedPreviews.splice(index, 1);
    setPreviewImages(updatedPreviews);
    
    // Also remove from the files array
    const updatedFiles = [...imageFiles];
    updatedFiles.splice(index, 1);
    setImageFiles(updatedFiles);
  };
  
  const removeExistingImage = (imageUrl) => {
    // Add to images to delete during form submission
    setImagesToDelete([...imagesToDelete, imageUrl]);
    
    // Remove from existing images display
    setExistingImages(existingImages.filter(img => img !== imageUrl));
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleAddTag = () => {
    if (tag.trim() && !tags.includes(tag.trim())) {
      setTags([...tags, tag.trim()]);
      setTag("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProductForm({
      ...productForm,
      [name]: value
    });
  };

  const uploadImagesToServer = async () => {
    if (imageFiles.length === 0) return [];
    
    const formData = new FormData();
    imageFiles.forEach(file => {
      formData.append("image", file);
    });

    try {
      const response = await axios.post(`${API_BASE_URL}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Handle response for both single and multiple images
      if (response.data.imageUrls) {
        return response.data.imageUrls;
      } else if (response.data.imageUrl) {
        return [response.data.imageUrl];
      }
      return [];
    } catch (error) {
      console.error("Image upload error:", error);
      throw new Error("Failed to upload images");
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    
    if (!productForm.name || !productForm.price) {
      alert("Name and Price are required!");
      return;
    }

    try {
      setIsLoading(true);
      
      // Upload new images if present
      let newUploadedImageUrls = [];
      if (imageFiles.length > 0) {
        newUploadedImageUrls = await uploadImagesToServer();
      }
      
      // Combine existing images (that weren't deleted) with newly uploaded ones
      const combinedImageUrls = [...existingImages, ...newUploadedImageUrls];
      
      // Prepare product data
      const productData = new FormData();
      
      // Add all form fields
      Object.entries(productForm).forEach(([key, value]) => {
        if (key !== 'imageUrl') { // Skip imageUrl field as we'll handle it separately
          productData.append(key, value);
        }
      });
      
      // Add tags
      productData.append('tags', JSON.stringify(tags));
      
      // Add image URLs
      combinedImageUrls.forEach(url => {
        productData.append('imageUrl', url);
      });
      
      // Add images to delete if updating
      if (editProduct && imagesToDelete.length > 0) {
        productData.append('imagesToDelete', JSON.stringify(imagesToDelete));
      }

      // Create or update product
      if (editProduct) {
        await axios.put(`${API_BASE_URL}/products/${editProduct.id}`, productData);
        alert("Product updated successfully!");
      } else {
        await axios.post(`${API_BASE_URL}/products`, productData);
        alert("Product added successfully!");
      }

      // Reset form
      setProductForm({
        name: "",
        capacity: "",
        materialType: "",
        glaze: "",
        firingTemp: "",
        discount: "0",
        mrp: "0",
        price: "0",
        dimensions: "",
        coneType: "",
        description: "",
        imageUrl: "",
        category: "",
        stock: "0",
      });
      setTags([]);
      setImageFiles([]);
      setPreviewImages([]);
      setExistingImages([]);
      setImagesToDelete([]);
      setEditProduct(null);
      
      // Refresh products
      fetchProducts();
      
      // Redirect if editing
      if (id) {
        navigate("/productadmin");
      }
    } catch (error) {
      console.error("Error adding/updating product:", error);
      alert("Error adding/updating product. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleProductDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;

    try {
      setIsLoading(true);
      await axios.delete(`${API_BASE_URL}/products/${id}`);
      setProducts(products.filter(p => p.id !== id));
      alert("Product deleted successfully!");
      setIsLoading(false);
    } catch (error) {
      console.error("Error deleting product:", error);
      alert("Error deleting product. Please try again.");
      setIsLoading(false);
    }
  };

  const handleTagKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  // Add to the component a function to handle clicking on a suggested tag
  const handleSuggestedTagClick = (suggestedTag) => {
    if (!tags.includes(suggestedTag)) {
      setTags([...tags, suggestedTag]);
    }
  };

  return (
    <Container>
      <PageTitle>{editProduct ? "Edit Product" : "Add New Product"}</PageTitle>

      <FormContainer>
        <FormGrid onSubmit={handleProductSubmit}>
          <FormSection>
            <h3>Basic Information</h3>
            
            <InputGroup>
              <Label>Product Name*</Label>
              <Input
                type="text"
                name="name"
                value={productForm.name}
                onChange={handleInputChange}
                placeholder="Enter product name"
                required
              />
            </InputGroup>
            
            <InputGroup>
              <Label>Description</Label>
              <TextArea
                name="description"
                value={productForm.description}
                onChange={handleInputChange}
                placeholder="Enter product description"
              />
            </InputGroup>
            
            <InputGroup>
              <Label>Category</Label>
              <Input
                type="text"
                name="category"
                value={productForm.category}
                onChange={handleInputChange}
                placeholder="Enter product category"
              />
            </InputGroup>
          </FormSection>
          
          <FormSection>
            <h3>Product Details</h3>
            
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
              <InputGroup>
                <Label>Material Type</Label>
                <Input
                  type="text"
                  name="materialType"
                  value={productForm.materialType}
                  onChange={handleInputChange}
                  placeholder="Material type"
                />
              </InputGroup>
              
              <InputGroup>
                <Label>Glaze</Label>
                <Input
                  type="text"
                  name="glaze"
                  value={productForm.glaze}
                  onChange={handleInputChange}
                  placeholder="Glaze type"
                />
              </InputGroup>
              
              <InputGroup>
                <Label>Cone Type</Label>
                <Input
                  type="text"
                  name="coneType"
                  value={productForm.coneType}
                  onChange={handleInputChange}
                  placeholder="Cone type"
                />
              </InputGroup>
              
              <InputGroup>
                <Label>Dimensions</Label>
                <Input
                  type="text"
                  name="dimensions"
                  value={productForm.dimensions}
                  onChange={handleInputChange}
                  placeholder="Dimensions (e.g., 10x15x5 cm)"
                />
              </InputGroup>
              
              <InputGroup>
                <Label>Capacity (ml)</Label>
                <Input
                  type="number"
                  name="capacity"
                  value={productForm.capacity}
                  onChange={handleInputChange}
                  placeholder="Capacity in ml"
                  min="0"
                  step="0.1"
                />
              </InputGroup>
              
              <InputGroup>
                <Label>Firing Temperature (°C)</Label>
                <Input
                  type="number"
                  name="firingTemp"
                  value={productForm.firingTemp}
                  onChange={handleInputChange}
                  placeholder="Firing temperature"
                  min="0"
                />
              </InputGroup>
            </div>
          </FormSection>
          
          <FormSection>
            <h3>Pricing & Inventory</h3>
            
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "20px" }}>
              <InputGroup>
                <Label>MRP (₹)*</Label>
                <Input
                  type="number"
                  name="mrp"
                  value={productForm.mrp}
                  onChange={handleInputChange}
                  placeholder="MRP"
                  min="0"
                  step="0.01"
                  required
                />
              </InputGroup>
              
              <InputGroup>
                <Label>Selling Price (₹)*</Label>
                <Input
                  type="number"
                  name="price"
                  value={productForm.price}
                  onChange={handleInputChange}
                  placeholder="Selling price"
                  min="0"
                  step="0.01"
                  required
                />
              </InputGroup>
              
              <InputGroup>
                <Label>Discount (%)</Label>
                <Input
                  type="number"
                  name="discount"
                  value={productForm.discount}
                  onChange={handleInputChange}
                  placeholder="Discount percentage"
                  min="0"
                  max="100"
                  step="0.1"
                />
              </InputGroup>
              
              <InputGroup>
                <Label>Stock Quantity</Label>
                <Input
                  type="number"
                  name="stock"
                  value={productForm.stock}
                  onChange={handleInputChange}
                  placeholder="Stock quantity"
                  min="0"
                />
              </InputGroup>
            </div>
          </FormSection>
          
          <FormSection>
            <h3>Product Images</h3>
            <p style={{ fontSize: "14px", color: "#666", marginBottom: "15px" }}>
              Upload high-quality images of your product (max 10 MB each)
            </p>
            
            <UploadButton type="button" onClick={handleUploadClick}>
              <FaUpload /> Choose Images
            </UploadButton>
            <input 
              type="file" 
              accept="image/*" 
              multiple
              name="image"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              ref={fileInputRef}
            />
            
            {/* Display new image previews */}
            {previewImages.length > 0 && (
              <>
                <Label>New Images</Label>
                <ImagePreviewContainer>
                  {previewImages.map((src, index) => (
                    <ImagePreviewItem key={`new-${index}`}>
                      <img src={src} alt={`New preview ${index + 1}`} />
                      <button 
                        type="button" 
                        className="remove-btn"
                        onClick={() => removePreviewImage(index)}
                      >
                        <FaTimes />
                      </button>
                    </ImagePreviewItem>
                  ))}
                </ImagePreviewContainer>
              </>
            )}
            
            {/* Display existing images */}
            {existingImages.length > 0 && (
              <>
                <Label>Existing Images</Label>
                <ImagePreviewContainer>
                  {existingImages.map((src, index) => (
                    <ImagePreviewItem key={`existing-${index}`}>
                      <img 
                        src={src} 
                        alt={`Existing ${index + 1}`} 
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/100?text=Error';
                        }}
                      />
                      <button 
                        type="button" 
                        className="remove-btn"
                        onClick={() => removeExistingImage(src)}
                      >
                        <FaTimes />
                      </button>
                    </ImagePreviewItem>
                  ))}
                </ImagePreviewContainer>
              </>
            )}
          </FormSection>
          
          <FormSection>
            <h3>Tags</h3>
            <p style={{ fontSize: "14px", color: "#666", marginBottom: "15px" }}>
              Add tags to help customers find your product (e.g., ceramic, handmade)
            </p>
            
            <TagInput>
              <input
                type="text"
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                onKeyPress={handleTagKeyPress}
                placeholder="Add a tag"
              />
              <button type="button" onClick={handleAddTag}>
                <FaPlus />
              </button>
            </TagInput>
            
            <TagsContainer>
              {tags.map((t, index) => (
                <Tag key={index}>
                  <FaTag size={10} />
                  {t}
                  <FaTimes size={12} onClick={() => handleRemoveTag(t)} />
                </Tag>
              ))}
            </TagsContainer>
            
            {/* Suggested Tags */}
            <SuggestedTagsContainer>
              <SuggestedTagsTitle>Suggested Tags:</SuggestedTagsTitle>
              <SuggestedTagsList>
                {SUGGESTED_TAGS
                  .filter(suggestedTag => !tags.includes(suggestedTag))
                  .slice(0, 15) // Show only 15 tags to avoid overwhelming
                  .map((suggestedTag, index) => (
                    <SuggestedTag 
                      key={index} 
                      onClick={() => handleSuggestedTagClick(suggestedTag)}
                    >
                      + {suggestedTag}
                    </SuggestedTag>
                  ))}
              </SuggestedTagsList>
            </SuggestedTagsContainer>
          </FormSection>
          
          <SubmitButton 
            type="submit" 
            disabled={isLoading || !productForm.name || !productForm.price}
            isEdit={!!editProduct}
            isLoading={isLoading}
          >
            {isLoading 
              ? "Processing..." 
              : editProduct 
                ? "Update Product" 
                : "Add Product"}
          </SubmitButton>
        </FormGrid>
      </FormContainer>

      <PageTitle>Product List</PageTitle>
      
      {isLoading && <LoadingIndicator>Loading products...</LoadingIndicator>}
      
      <ProductList>
        {products.map((product) => (
          <ProductCard key={product.id}>
            <ProductImage>
              <img 
                src={(product.imageUrl && (Array.isArray(product.imageUrl) ? product.imageUrl[0] : product.imageUrl)) || '/placeholder.jpg'} 
                alt={product.name}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/300x200?text=No+Image';
                }}
              />
              {Array.isArray(product.imageUrl) && product.imageUrl.length > 1 && (
                <span className="image-count">+{product.imageUrl.length - 1}</span>
              )}
            </ProductImage>
            
            <ProductInfo>
              <ProductTitle>{product.name}</ProductTitle>
              <ProductPrice>₹{product.price}</ProductPrice>
              <ProductDescription>{product.description}</ProductDescription>
              
              <div style={{ display: "flex", gap: "5px", flexWrap: "wrap", marginTop: "10px" }}>
                {product.tags && product.tags.map((tag, index) => (
                  <span 
                    key={index}
                    style={{
                      fontSize: "12px",
                      padding: "3px 8px",
                      background: "#f0f0f0",
                      borderRadius: "30px",
                      color: "#666"
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
              
              <ActionButtons>
                <ActionButton 
                  variant="edit" 
                  onClick={() => navigate(`/edit-product/${product.id}`)}
                >
                  <FaEdit /> Edit
                </ActionButton>
                
                <ActionButton 
                  variant="delete" 
                  onClick={() => handleProductDelete(product.id)}
                >
                  <FaTrash /> Delete
                </ActionButton>
              </ActionButtons>
            </ProductInfo>
          </ProductCard>
        ))}
      </ProductList>
    </Container>
  );
};

export default ProductAdmin;